import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABLanguageSelect, ABCitySelect, ABVenueSelect } from '../lib/ab/select-admin';
import { ab_transfer_destination_kind_values, ABInputArray, ABObjectCount, ABPicturesArray, makeTransferDestinationInput } from '../lib/ab/widgets-admin';
import { CBookingAdminView } from '../lib/mdl/c-booking-admin';
import { c_cities } from '../lib/mdl/c-city';
import { cCountryDefaultCurrency } from '../lib/mdl/c-country';
import { cSessionDefaultInstructions } from '../lib/mdl/c-event-admin';
import { c_event_tags, cEventTagName } from '../lib/mdl/c-event-tag';
import { CProducer, c_producers, CTicketType, cProducerRoleInviteSend, cProducerRoleInviteResend, cProducerRoleInviteDelete, cProducerSendMissingInfo, CProducerId } from '../lib/mdl/c-producer';
import { cProducerBookingsWithPurchases, CProducerRole, cProducerRoleDelete, CProducerRoleInviteUserView, cProducerRolesGet, cProducerRoleUpdate, cProducerUpdateEventDefaults, cProducerUsersGetAll, CUserProducerRoleUserView, CUserProducerView } from '../lib/mdl/c-producer-admin';
import { cUserFullName } from '../lib/mdl/c-user';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { the_alt_token, the_user } from '../lib/me/login';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UITextInput, UINumberInput, UIFixedCurrencyInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIMarkdownInput } from '../lib/ui/component/markdown';
import { UISelect } from '../lib/ui/component/select';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UIExclusiveToggles, UIRadio, UICheckbox, UIFlagsToggles } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { $time } from '../lib/y/time';
import { YId, YFlags } from '../lib/y/types';

class ProducerInfoTab extends UIView {
    constructor(producer?: CProducer) {
        super('div', 'tl-admin-page');

        const name = new UITextInput({ required: true });
        const email = new UITextInput({ type: 'email', required: true });
        const phone = new UITextInput({ type: 'phone', required: true });
        const language = new ABLanguageSelect({ required: true })

        const org_name = new UITextInput();
        const org_number = new UITextInput();
        const tax_id = new UITextInput();
        const payout_destination_kind = new UIExclusiveToggles(ab_transfer_destination_kind_values, v => new UIRadio(v), { classes: 'l-toggle-row' });
        const payout_destination_number = makeTransferDestinationInput(payout_destination_kind);

        const address = new UITextInput({ lines: 3 });

        const merge_booking_lists = new UICheckbox({ title: 'Merge booking lists' });

        const form = $form({ _: [merge_booking_lists], name, email, phone, language, org_name, org_number, tax_id, payout_destination: $form({ kind: payout_destination_kind, number: payout_destination_number }), address });

        const send_missing_info_mail = new UIButton({
            kind: 'k-text', title: 'Ask producer to fill missing details', on_click: () => {
                cProducerSendMissingInfo(producer!.id).then(() => {
                    uiToast('Email sent');
                });
            }
        });

        if(producer) {
            form.value = producer;
            merge_booking_lists.value = !!producer.flags.merge_booking_lists;
        }

        const section = $el.FORM('ui-section w-form l-form', [
            producer && new UILabeled($el.B(producer.id), { above: 'ID' }),
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(email, { above: 'Email' }),
            new UILabeled(phone, { above: 'Phone number' }),
            new UILabeled(language, { above: 'Language' }),
            $el.H3('heading', 'Invoice information'),
            new UILabeled(org_name, { above: 'Company name' }),
            new UILabeled(org_number, { above: 'Org. number' }),
            new UILabeled(tax_id, { above: 'VAT ID' }),
            new UILabeled(payout_destination_kind, { above: 'Payout account' }),
            payout_destination_number,
            new UILabeled(address, { above: 'Address' }),
            merge_booking_lists,
            producer && send_missing_info_mail
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                const value = form.value;
                value.flags = {
                    merge_booking_lists: merge_booking_lists.value ? true : undefined
                };

                c_producers.upsert(producer?.id, value).then($location.pop, uiToast);
            }
        });

        addTooltip(producer ? 'Update' : 'Add', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);
    }
}

const role_names = {
    admin: 'Administrator',
    events: 'Manage events',
    bookings: 'See bookings',
    economy: 'Economy'
}

function rolesToDescription(roles: CProducerRole[]) {
    return roles.map(r => role_names[r]).filter(r => r).join(', ');
}

class InviteScreen extends UIScreen {
    constructor(private _producer_id: YId, private _role?: CUserProducerRoleUserView) {
        super();
        this.title = _role ? _role.user_first_name + ' ' + _role.user_last_name : 'Lägg till användare';
    }

    protected override fillContent(content: UIViewSpec): void {
        const producer_id = this._producer_id;
        const role = this._role;

        const section = $el.FORM('ui-section w-form l-form');

        const admin_values = [
            { key: 'admin', title: 'Admin', subtitle: 'Har full åtkomst och kan lägga till samt ta bort användare' },
            { key: 'others', title: 'Begränsade behörigheter' },
        ];

        const admin = new UIExclusiveToggles(admin_values, v => new UIRadio(v), { classes: 'l-form' })

        const role_values = [
            { key: 'events' as CProducerRole, title: 'Evenemang', subtitle: 'Kan lägga upp och hantera dina evenemang' },
            { key: 'bookings' as CProducerRole, title: 'Bokning', subtitle: 'Kan se och hantera bokningslistor' },
            { key: 'economy' as CProducerRole, title: 'Ekonomi', subtitle: 'Kan se och hantera ekonomiska nyckeltal samt avräkningar' }
        ];

        const roles = new UIFlagsToggles(role_values, v => new UICheckbox(v), { required: true, classes: 'l-form tl-permission-box' });

        const form = $form({});
        let email: UITextInput | undefined;

        if(!role) {
            email = new UITextInput({ type: 'email', required: true });
            form.add({ email: email });

            section.ui_add([
                $el.H3('heading', "Lägg till användare till ditt team"),
                new UILabeled(email, { above: "E-postadress" }),
            ]);
        }

        const button = new UIButton({ icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button' });
        addTooltip(role ? 'Spara' : 'Skicka inbjudan', button, { offset: 8, hover: button, position: UIRelPos.Above });

        $on(admin, 'value', v => {
            roles.disabled = v === 'admin';
        });

        if(role) {
            admin.value = 'others';

            if(role.roles['admin'])
                admin.value = 'admin';
            else
                roles.value = role.roles;

            button.on_click = () => {
                if(!form.validate()) return;
                const roles_val: YFlags<CProducerRole> = admin.value === 'admin' ? { admin: true } : roles.value;
                if(!Object.keys(roles_val).length) {
                    roles.markInvalid(true, true);
                    return;
                }
                cProducerRoleUpdate(role, roles_val).then(() => {
                    $location.pop();
                }, uiToast);
            };
        } else {
            button.on_click = () => {
                if(!form.validate()) return;
                const email_val = email!.value;
                const roles_val: YFlags<CProducerRole> = admin.value === 'admin' ? { admin: true } : roles.value;
                if(!Object.keys(roles_val).length) {
                    roles.markInvalid(true, true);
                    return;
                }
                cProducerRoleInviteSend(email_val, producer_id, Object.keys(roles_val)).then($location.pop, uiToast);
            };

            admin.value = 'admin';
        }

        section.ui_add([
            new UILabeled(admin, { above: "Behörigheter" }),
            roles
        ]);

        content.ui_add([section, button]);
    }
}

class ProducerDefaultsTab extends UIView {
    constructor(producer: CProducer) {
        super('div', 'tl-admin-page');

        const city_id = new ABCitySelect;
        const venue_id = new ABVenueSelect;
        $on(city_id, 'value', city => {
            if(city) {
                venue_id.city = city;
                base_price.value = { value: base_price.value.value, currency: cCountryDefaultCurrency(c_cities.maybe(city)!.country) };
                payout_price.value = { value: payout_price.value.value, currency: cCountryDefaultCurrency(c_cities.maybe(city)!.country) };
            }
        });
        const vat = new UINumberInput();
        const event_tag_values = c_event_tags.all.filter(t => !t.dynamic && !t.is_collection).map(t => ({ key: t.id, name: cEventTagName(t) })).sort((a, b) => a.name.localeCompare(b.name));
        const event_tag_ids = new ABInputArray('+ Add tag', () => new UISelect(event_tag_values, { searchable: true }), event_tag_values[0].key);
        const description = new UII18NInput(new UIMarkdownInput({ lines: 8, resizeable: true }), { translator: uInputTranslator });

        const no_market = new UICheckbox({ title: 'Don\'t market outside Abundo' });
        const no_past = new UICheckbox({ title: 'Don\'t show in "past events"' });

        const picture_ids = new ABPicturesArray('+ Add picture', 'event', false, true);
        // const bookings_emails = new ABInputArray('+ Add email');

        const is_drop_in = new UICheckbox({ title: 'Drop in' });
        const sales_end = new UINumberInput({ decimal: true });
        const no_buy_more = new UICheckbox({ title: 'Disable "buy more" for this session' });
        const ticket_values: UIValue<CTicketType>[] = [{ key: 'pickup', name: 'Pickup' }, { key: 'email', name: 'E-ticket' }];
        const ticket_type = new UISelect(ticket_values);
        const instructions = new UII18NInput(new UITextInput({ lines: 3 }), { translator: uInputTranslator })
        const set_default_instructions = new UIButton({
            kind: 'k-text', classes: 'm-inline ui-input-helper', title: 'Insert standard text', on_click: () => {
                instructions.value = cSessionDefaultInstructions(ticket_type.value);
            }
        });
        const base_price = new UIFixedCurrencyInput({ default_currency: 'SEK' });
        const payout_price = new UIFixedCurrencyInput({ default_currency: 'SEK' });

        // const media_youtube = new UITextInput({ autofill: 'youtube-id', placeholder: 'Youtube', l_deco: $el.ICON(UIIcon.BRAND_YOUTUBE) });
        // const media_vimeo = new UITextInput({ autofill: 'vimeo-id', placeholder: 'Vimeo', l_deco: $el.ICON(UIIcon.BRAND_VIMEO) });
        // const media_spotify = new UITextInput({ autofill: 'spotify-id', placeholder: 'Spotify', l_deco: $el.ICON(UIIcon.BRAND_SPOTIFY) });
        // const media_soundcloud = new UITextInput({ autofill: 'soundcloud-id', placeholder: 'Soundcloud', l_deco: $el.ICON(UIIcon.BRAND_SOUNDCLOUD) });

        // const links_form = $form({ youtube: media_youtube, vimeo: media_vimeo, spotify: media_spotify, soundcloud: media_soundcloud });

        const form = $form({ _: [no_market, no_past, no_buy_more], vat, city_id, venue_id, event_tag_ids: event_tag_ids.adapter, description, picture_ids: picture_ids.adapter, is_drop_in, sales_end, ticket_type, instructions, base_price, payout_price });

        const section = $el.FORM('ui-section w-form l-form', [
            $el.H3('heading', 'Event data'),
            new UILabeled(city_id, { above: 'City' }),
            new UILabeled(venue_id, { above: 'Venue' }),
            new UILabeled(vat, { above: 'Default VAT (%)' }),
            new UILabeled(event_tag_ids, { above: 'Tags' }),
            event_tag_ids.add_button,
            new UILabeled(description, { above: 'About the event' }),
            // new UILabeled(media_youtube, { above: 'Media links' }),
            // media_vimeo,
            // media_spotify,
            // media_soundcloud,
            no_market,
            no_past,
            new UILabeled(picture_ids, { above: 'Pictures' }),
            picture_ids.add_button,
            // new UILabeled(bookings_emails, { above: 'Send booking list to (if different from producer email)' }),
            // bookings_emails.add_button,
            $el.H3('heading', 'Session data'),
            $el.P('input-hint', 'Will be used for the first session created'),
            is_drop_in,
            new UILabeled(sales_end, { above: 'Sales end (hours before event)' }),
            no_buy_more,
            new UILabeled(ticket_type, { above: 'Ticket type' }),
            new UILabeled(instructions, { above: 'Booking instructions' }),
            set_default_instructions,
            new UILabeled(base_price, { above: 'Regular price' }),
            new UILabeled(payout_price, { above: 'Payout amount' }),
        ]);

        const button = new UIButton({ icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button' });
        addTooltip('Save', button, { offset: 8, hover: button, position: UIRelPos.Above });

        button.on_click = () => {
            if(!form.validate()) return;
            const value = form.value;
            value.event_flags = {
                no_market: no_market.value ? true : undefined,
                no_past: no_past.value ? true : undefined
            }
            value.session_flags = {
                no_buy_more: no_buy_more.value ? true : undefined
            }
            return cProducerUpdateEventDefaults(producer, value).then($fn.TRUE, uiToast);
        };

        const defaults = producer.event_defaults || {};
        form.value = defaults;
        no_market.value = !!defaults.event_flags?.no_market;
        no_past.value = !!defaults.event_flags?.no_past;
        no_buy_more.value = !!defaults.session_flags?.no_buy_more;

        this.ui_add([section, button]);
    }
}

class ProducerUsersTab extends UIView {
    private _roles_source: YArrayDataSource<CUserProducerRoleUserView>;
    private _invites_source: YArrayDataSource<CProducerRoleInviteUserView>;
    private _roles?: CUserProducerRoleUserView[];
    private _invites?: CProducerRoleInviteUserView[];

    constructor(producer: CProducer) {
        super('div', 'tl-admin-page');

        const roles_def = new UITableDefinition<CUserProducerRoleUserView>();
        roles_def.addValue(r => r.user_first_name + ' ' + r.user_last_name, { title: 'Name', width: 160 });
        roles_def.addValue(r => r.user_email, { title: 'Email', width: 160 });
        roles_def.addValue(r => rolesToDescription(Object.keys(r.roles)), { title: 'Permissions', width: 160 });
        roles_def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: r => {
                    $location.push({ transient: new InviteScreen(producer.id, r) });
                }, when: r => r.user_id != the_user!.id
            },
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Remove', action: r => {
                    showWarning({ text: 'Är du säker?', cancel_text: 'Avbryt', ok_text: 'Bekräfta' }).then(() => {
                        cProducerRoleDelete(producer.id, r.user_id).then(() => {
                            this._roles = this._roles!.filter(o => o != r);
                            this._doSearch();
                        });
                    });
                }
            }
        ], { width: -62 });

        const invites_def = new UITableDefinition<CProducerRoleInviteUserView>();
        invites_def.addValue(i => i.dst_email, { title: 'Email', width: 160 });
        invites_def.addValue(i => i.src_user_first_name + ' ' + i.src_user_last_name, { title: 'Sent by', width: 120 });
        invites_def.addValue(i => rolesToDescription(Object.keys(i.roles)), { title: 'Permissions', width: 160 });
        invites_def.addButtons([
            { icon: UIIcon.SHARE, tooltip: 'Resend', action: i => cProducerRoleInviteResend(i.id).then($fn.TRUE) },
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Remove', action: invite => {
                    cProducerRoleInviteDelete(invite.id).then(() => {
                        this._invites = this._invites!.filter(i => i.id != invite.id);
                        this._doSearch();
                    });
                }
            }
        ], { width: -62 });

        const section = $el.FORM('ui-section w-form l-form', [
            new UITable(roles_def, this._roles_source = new YArrayDataSource),
            new UIButton({
                kind: 'k-text', title: '+ Invite user', on_click: () => {
                    $location.push({ transient: new InviteScreen(producer.id) });
                }
            }),
            $el.H3('heading', 'Invited users'),
            new UITable(invites_def, this._invites_source = new YArrayDataSource)
        ]);

        cProducerRolesGet(producer.id).then(response => {
            this._roles = response.roles;
            this._invites = response.invites;
            this._doSearch();
        })

        this.ui_add(section);
    }

    private _doSearch() {
        if(!this._roles || !this._invites) return;

        this._roles_source.data = this._roles;
        this._invites_source.data = this._invites;
    }
}

class ProducerBookingsTab extends UIView {
    private _source: YArrayDataSource<CBookingAdminView>;
    private _bookings?: CBookingAdminView[];

    constructor(producer: CProducer) {
        super('div', 'tl-admin-page');

        const def = new UITableDefinition<CBookingAdminView>();
        def.addValue(b => b.user_first_name + ' ' + b.user_last_name, { title: 'Name', width: 160 });
        def.addValue(b => b.user_email, { title: 'Email', width: 160 });
        def.addValue(b => b.event_name, { title: 'Event', width: 160 });
        def.addValue(b => $time(b.session_time), $fmt.DATE(), { title: 'Session', width: -120 });
        def.addValue(b => b.quantity, $fmt.NUMBER, { title: 'Amount', width: -100 });

        cProducerBookingsWithPurchases(producer.id).then(bookings => {
            this._bookings = bookings;
            this._doSearch();
        });

        this.ui_add($el.DIV('ui-section w-block l-form', [
            new UITable(def, this._source = new YArrayDataSource),
        ]));
    }

    private _doSearch() {
        if(!this._bookings) return;

        this._source.data = this._bookings;
    }
}

export class AdminProducerScreen extends UIScreen {
    constructor(private _producer_id?: CProducerId) {
        super();
        this.title = _producer_id ? 'Prdoucer' : 'New producer';
    }

    protected override fillContent(content: UIViewSpec): void {
        content.classList.add('tl-tabs');

        if(this._producer_id) {
            c_producers.get(this._producer_id).then(producer => {
                this.title = producer.name;

                content.ui_add(new UITabs([
                    { title: 'Info', view: () => new ProducerInfoTab(producer) },
                    { title: 'Event defaults', view: () => new ProducerDefaultsTab(producer) },
                    { title: 'Users', view: () => new ProducerUsersTab(producer) },
                    { title: 'Purchased Tickets', view: () => new ProducerBookingsTab(producer) }
                ]));
            })
        } else {
            content.ui_add(new UITabs([
                { title: 'Info', view: () => new ProducerInfoTab }
            ]));
        }
    }
}

const language_name = {
    sv: 'Swedish',
    en: 'English',
    de: 'German',
    es: 'Spanish',
    ca: 'Catalan',
    fr: 'French'
}

export class AdminProducersScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec) {
        const doSearch = () => {
            let producers = c_producers.all;

            producers = filter_bar.search_input!.filterArray(producers, (p, w) => {
                if(p.name.toLowerCase().includes(w)) return true;
                if(p.email?.toLowerCase().includes(w)) return true;
                if(p.phone?.toLowerCase().includes(w)) return true;
                if(p.slug.toLowerCase().includes(w)) return true;
                return false;
            });

            count.number = producers.length;
            source.data = producers;
        }

        const stats_download = new UIButton({ kind: 'k-filter', icon: UIIcon.DOWNLOAD, title: 'Statistics', link: `/docs/producers/excel?token=${the_alt_token}` });
        const count = new ABObjectCount('producers');

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            right_content: [stats_download, count],
            no_header: true
        });

        const def = new UITableDefinition<CProducer>();
        def.addValue(p => p.name, { title: 'Name', width: 200 });
        def.addValue(p => p.email ?? '-', { title: 'Email', width: 200 });
        def.addValue(p => language_name[p.language], { title: 'Language', width: -120 });
        def.addValue(p => $time(p.created_at), $fmt.DATE('yyyy-MM-dd', '-'), { title: 'Created at', width: -120 });
        def.addValue(p => p.event_defaults?.session_flags?.no_buy_more || false, $fmt.CHECKMARK, { title: 'Disable \'buy more\'', width: -140 });
        def.addValue(p => p.event_defaults?.vat ?? 0, $fmt.NUMBER, { title: 'Default VAT', width: -120 });

        def.addButtons([
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('producer', { producer_id: item.id });
                }
            },
            { icon: UIIcon.EYE, tooltip: 'Events page', link: i => 'https://abundolive.se/producer/' + i.slug }
        ], { width: -60 });

        def.preference_prefix = 'producers';

        const source = new YArrayDataSource<CProducer>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wide', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New producer', () => { $location.push('producer') })
        ]);

        doSearch();
    }
}

function getProducerName(user: CUserProducerView) {
    let ret = '';

    for(const r of user.producer_roles || []) {
        const producer = c_producers.maybe(r.producer_id);
        if(producer) {
            if(ret.length) ret += ', ';
            ret += producer.name;
        }
    }

    return ret;
}

export class AdminProducerUsersScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const doSearch = () => {
            let users = all_users.filter(u => u.producer_roles?.length);
            users = filter_bar.search_input!.filterArray(users, (u, w) => {
                if(u.first_name.toLowerCase().includes(w)) return true;
                if(u.last_name.toLowerCase().includes(w)) return true;
                if(u.email?.toLowerCase().includes(w)) return true;
                for(const r of u.producer_roles || []) {
                    const producer = c_producers.maybe(r.producer_id);
                    if(producer && producer.name.toLowerCase().includes(w)) return true;
                }
                return false;
            });

            count.number = users.length;
            source.data = users;
        }

        const doRefresh = async () => {
            all_users = await cProducerUsersGetAll(true)
            doSearch();
        }

        const excel_download = new UIButton({ kind: 'k-filter', icon: UIIcon.DOWNLOAD, title: 'Excel', link: `/docs/producers/users/excel?token=${the_alt_token}` });
        const count = new ABObjectCount('users');

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            on_filter: doSearch,
            on_refresh: doRefresh,
            right_content: [excel_download, count],
            no_header: true
        });

        const def = new UITableDefinition<CUserProducerView>();
        def.addValue(cUserFullName, { title: 'Name', width: 160 });
        def.addValue(u => u.email!, { title: 'Email', width: 160 });
        def.addValue(getProducerName, { title: 'Producer', width: 120 });
        def.addValue(u => $time(u.last_login_at), $fmt.DATE(), { title: 'Last login', width: -120 });

        def.addButtons([
            { icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => { $location.push('user', { user_id: item.id }) } },
            { icon: UIIcon.EYE, tooltip: 'View as producer', link: u => `https://producer.abundolive.se?as_user=${u.as_user_token}` }
        ], { width: -62 });

        def.preference_prefix = 'producer_users';

        const source = new YArrayDataSource<CUserProducerView>();
        const table = new UITable(def, source);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                table,
            ])),
        ]);

        let all_users = await cProducerUsersGetAll();
        doSearch();
    }
}